import { Plus, Search, X } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/controls'
import { Badge, Panel, PanelHeader } from '@/components/ui/display'
import { Input } from '@/components/ui/field'
import { MAX_ADDED_RECIPIENTS, searchDirectory, type ShareRecipient } from '@/data/externalShare'
import type { Person } from '@/data/types'
import { cn } from '@/lib/utils'

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/**
 * Who the meeting reaches.
 *
 * The search field FILTERS THE LIST rather than opening a menu over it. The
 * old page floated results above the recipients, which meant the two halves of
 * one question — "is Rachel already on here?" and "who else should be?" —
 * could not be looked at together, and every keystroke redrew a layer on top
 * of the answer to the first. Here typing narrows the rows in place and puts
 * whatever it can add at the top of the same list, so there is one list, in one
 * place, at all times.
 *
 * People typed in by hand are ordinary rows too, not a second collection
 * rendered underneath in a different shape. They carry a badge saying where
 * they came from and an × that takes them off again.
 */
export function RecipientList({
  token,
  recipients,
  selected,
  onToggle,
  onAdd,
  onRemove,
  busy,
}: {
  /** Scopes the directory search; this page has no session to do it with. */
  token: string
  recipients: ShareRecipient[]
  selected: Set<string>
  onToggle: (email: string) => void
  onAdd: (person: Person) => void
  onRemove: (email: string) => void
  busy?: boolean
}) {
  const [query, setQuery] = useState('')
  const [error, setError] = useState<string | null>(null)

  const normalized = query.trim().toLowerCase()
  const known = useMemo(
    () => new Set(recipients.map((person) => person.email)),
    [recipients]
  )
  const addedCount = recipients.filter((person) => !person.onInvite).length

  const visible = recipients.filter(
    (person) =>
      !normalized ||
      person.name.toLowerCase().includes(normalized) ||
      person.email.includes(normalized)
  )

  // The directory is a request now, not a filter over a local list. Keyed on
  // the query so each keystroke's answer belongs to the text that asked for it,
  // and `[]` while in flight so the "add this address" row does not flicker in
  // and out between them.
  const matches =
    useQuery({
      queryKey: ['share-directory', token, normalized],
      queryFn: () => searchDirectory(token, normalized, known),
      enabled: normalized.length >= 2,
    }).data ?? []
  const typedIsNew = EMAIL.test(normalized) && !known.has(normalized)
  const offersTyped = typedIsNew && !matches.some((person) => person.email === normalized)

  function add(person: Person) {
    const email = person.email.trim().toLowerCase()
    if (!EMAIL.test(email)) {
      setError('Enter a complete email address.')
      return
    }
    if (known.has(email)) {
      setError('That address is already on the list.')
      return
    }
    if (addedCount >= MAX_ADDED_RECIPIENTS) {
      setError(`You can add at most ${MAX_ADDED_RECIPIENTS} addresses.`)
      return
    }
    onAdd({ ...person, email })
    setQuery('')
    setError(null)
  }

  return (
    <Panel>
      <PanelHeader
        title="Recipients"
        meta={`${selected.size} of ${recipients.length} selected`}
      />

      <div className="relative border-b border-[var(--color-border)] p-2">
        <Search className="pointer-events-none absolute top-1/2 left-4.5 size-3.5 -translate-y-1/2 text-[var(--color-fg-subtle)]" />
        <Input
          value={query}
          disabled={busy}
          className="h-8 pl-8 text-xs"
          placeholder="Filter, or type an email address to add someone…"
          aria-label="Filter recipients or add an email address"
          onChange={(event) => {
            setQuery(event.target.value)
            setError(null)
          }}
          onKeyDown={(event) => {
            if (event.key !== 'Enter') return
            event.preventDefault()
            // Enter takes the one obvious match, otherwise whatever was typed,
            // so a full address never needs a second click.
            if (matches.length === 1) add(matches[0]!)
            else if (typedIsNew) add({ email: normalized, name: normalized, isInternal: false })
          }}
        />
      </div>

      <ul className="divide-y divide-[var(--color-border-subtle)]">
        {matches.map((person) => (
          <AddableRow key={`match-${person.email}`} person={person} onAdd={() => add(person)} />
        ))}
        {offersTyped ? (
          <AddableRow
            person={{ email: normalized, name: normalized, isInternal: false }}
            label={`Add “${normalized}”`}
            onAdd={() => add({ email: normalized, name: normalized, isInternal: false })}
          />
        ) : null}

        {visible.map((person) => (
          <RecipientRow
            key={person.email}
            person={person}
            checked={selected.has(person.email)}
            busy={busy}
            onToggle={() => onToggle(person.email)}
            onRemove={person.onInvite ? undefined : () => onRemove(person.email)}
          />
        ))}

        {visible.length === 0 && matches.length === 0 && !offersTyped ? (
          <li className="px-3 py-8 text-center text-2xs text-[var(--color-fg-subtle)]">
            {normalized
              ? 'Nobody matches. Type a full email address to add someone new.'
              : // The recap carries this link whether or not the meeting had
                // guests, so an empty list is a normal arrival and has to point
                // at the field above rather than look broken.
                'Nobody external was on this invite. Use the field above to choose who gets access.'}
          </li>
        ) : null}
      </ul>

      {error ? (
        <p className="border-t border-[var(--color-border)] px-3 py-1.5 text-2xs text-[var(--color-danger)]" role="alert">
          {error}
        </p>
      ) : null}
    </Panel>
  )
}

function RecipientRow({
  person,
  checked,
  onToggle,
  onRemove,
  busy,
}: {
  person: ShareRecipient
  checked: boolean
  onToggle: () => void
  onRemove?: () => void
  busy?: boolean
}) {
  const named = person.name !== person.email

  return (
    <li>
      {/* A label, so the whole row is the target. The × and the checkbox are
          interactive content, which the label does not forward to — clicking
          either does its own thing exactly once. */}
      <label
        className={cn(
          'flex cursor-pointer items-center gap-2.5 px-3 py-2',
          'hover:bg-[var(--color-surface-hover)]'
        )}
      >
        <Checkbox
          checked={checked}
          disabled={busy}
          onCheckedChange={onToggle}
          aria-label={`Share with ${person.name}`}
        />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-xs text-[var(--color-fg)]">{person.name}</span>
          {named ? (
            <span className="block truncate text-2xs text-[var(--color-fg-subtle)]">
              {person.email}
            </span>
          ) : null}
        </span>

        {person.alreadyShared ? (
          <Badge tone="neutral" className="shrink-0">
            has access
          </Badge>
        ) : null}
        {!person.onInvite ? (
          <Badge tone="outline" className="shrink-0">
            added
          </Badge>
        ) : null}

        {onRemove ? (
          <Button
            size="iconSm"
            variant="ghost"
            disabled={busy}
            aria-label={`Take ${person.email} off the list`}
            onClick={onRemove}
          >
            <X className="size-3" />
          </Button>
        ) : null}
      </label>
    </li>
  )
}

/** A person the field found who is not on the list yet. */
function AddableRow({
  person,
  label,
  onAdd,
}: {
  person: Person
  label?: string
  onAdd: () => void
}) {
  return (
    <li className="bg-[var(--color-surface-sunken)]">
      <button
        type="button"
        onClick={onAdd}
        aria-label={`Add ${person.email}`}
        className="flex w-full items-center gap-2.5 px-3 py-2 text-left hover:bg-[var(--color-surface-hover)]"
      >
        <Plus className="size-3.5 shrink-0 text-[var(--color-fg-subtle)]" />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-xs text-[var(--color-fg)]">
            {label ?? person.name}
          </span>
          {!label ? (
            <span className="block truncate text-2xs text-[var(--color-fg-subtle)]">
              {person.email}
            </span>
          ) : null}
        </span>
        <span className="shrink-0 text-2xs text-[var(--color-fg-subtle)]">
          {person.isInternal ? 'internal' : 'external'}
        </span>
      </button>
    </li>
  )
}
